import React, { useMemo, useState, useEffect } from 'react';
import { Reorder, motion, AnimatePresence } from 'framer-motion';
import ConsequenceReveal from '../shared/ConsequenceReveal';
import styles from './LessonBuilderEngine.module.css';

const BLOOM_TIERS = [
  { name: 'Remember', color: '#e63946', description: 'Recall facts' },
  { name: 'Understand', color: '#f4a261', description: 'Explain concepts' },
  { name: 'Apply', color: '#e9c46a', description: 'Use in new situations' },
  { name: 'Analyze', color: '#2a9d8f', description: 'Break down ideas' },
  { name: 'Evaluate', color: '#457b9d', description: 'Justify decisions' },
  { name: 'Create', color: '#6a4c93', description: 'Produce new work' }
];

const LessonBuilderEngine = ({ gamifiedQuestion, onAnswer, accent = '#2a9d8f' }) => {
  const [selected, setSelected] = useState(null);
  const [showOutcome, setShowOutcome] = useState(false);
  const [ranked, setRanked] = useState([]);
  const [hasReordered, setHasReordered] = useState(false);
  const [sceneReady, setSceneReady] = useState(false);

  const options = useMemo(() =>
    gamifiedQuestion?.options?.slice(0, 5) || [],
    [gamifiedQuestion?.options]
  );

  const selectedOption = options.find((option) => option.label === selected);
  const gameType = String(gamifiedQuestion?.gameType || '').toUpperCase();
  const isPriorityMode = gameType === 'PRIORITY_DRAG' || gameType === 'SORT_BIN' || gameType === 'TIMELINE_DROP';
  const isBlooms = String(gamifiedQuestion?.sceneType || '').includes('bloom');

  useEffect(() => {
    setRanked(options);
    const timer = setTimeout(() => setSceneReady(true), 300);
    return () => clearTimeout(timer);
  }, [options]);

  const handleSelect = (label) => {
    if (!sceneReady || selected) return;
    setSelected(label);
    setTimeout(() => setShowOutcome(true), 800);
  };

  const handleReorder = (newOrder) => {
    setRanked(newOrder);
    if (!hasReordered) setHasReordered(true);
  };

  const handleSubmit = () => {
    if (!hasReordered || selected) return;
    setSelected(ranked[0]?.label);
    setTimeout(() => setShowOutcome(true), 600);
  };

  return (
    <div className={styles.engine} style={{ '--accent': accent }}>
      {/* SCENE AREA - Top */}
      <div className={styles.sceneArea}>
        {isBlooms ? (
          /* Bloom's Pyramid Visualization */
          <motion.svg
            viewBox="0 0 400 200"
            className={styles.sceneSvg}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            {/* Pyramid tiers from bottom to top */}
            {BLOOM_TIERS.slice(0, 4).reverse().map((tier, index) => (
              <motion.g
                key={tier.name}
                initial={{ opacity: 0, scaleX: 0 }}
                animate={{ opacity: 1, scaleX: 1 }}
                transition={{ delay: 0.1 * index, duration: 0.4 }}
                style={{ transformOrigin: 'center' }}
              >
                <polygon
                  points={`${120 + index * 20},${160 - index * 35} ${280 - index * 20},${160 - index * 35} ${260 - index * 20},${125 - index * 35} ${140 + index * 20},${125 - index * 35}`}
                  fill={tier.color}
                  opacity="0.3"
                  stroke={tier.color}
                  strokeWidth="2"
                />
                <text
                  x="200"
                  y={142 - index * 35}
                  textAnchor="middle"
                  fontSize="12"
                  fontWeight="600"
                  fill="#1a1a1a"
                >
                  {tier.name}
                </text>
              </motion.g>
            ))}
            {/* Teacher figure */}
            <motion.g
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.5 }}
            >
              <circle cx="340" cy="100" r="15" fill="#fff" stroke="#1a1a1a" strokeWidth="2" />
              <line x1="340" y1="115" x2="340" y2="160" stroke="#1a1a1a" strokeWidth="2" />
              <text x="340" y="180" textAnchor="middle" fontSize="10" fill="#666">Teacher</text>
            </motion.g>
          </motion.svg>
        ) : isPriorityMode ? (
          /* Priority/Timeline Scene */
          <motion.div
            className={styles.priorityScene}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <svg viewBox="0 0 400 120" className={styles.sceneSvg}>
              {/* Timeline or Priority visualization */}
              <motion.line
                x1="50"
                y1="60"
                x2="350"
                y2="60"
                stroke="#1a1a1a"
                strokeWidth="3"
                strokeLinecap="round"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ duration: 0.8 }}
              />
              {/* Timeline markers */}
              {[1, 2, 3, 4].map((i) => (
                <motion.circle
                  key={i}
                  cx={50 + i * 75}
                  cy="60"
                  r="8"
                  fill="#fff"
                  stroke="#1a1a1a"
                  strokeWidth="2"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.3 + i * 0.1, type: 'spring' }}
                />
              ))}
              {/* Clock icon for priority */}
              <motion.g
                initial={{ opacity: 0, rotate: -30 }}
                animate={{ opacity: 1, rotate: 0 }}
                transition={{ delay: 0.8, type: 'spring' }}
              >
                <circle cx="350" cy="100" r="15" fill="#fff" stroke="#1a1a1a" strokeWidth="2" />
                <line x1="350" y1="100" x2="350" y2="90" stroke="#1a1a1a" strokeWidth="2" />
                <line x1="350" y1="100" x2="358" y2="100" stroke="#1a1a1a" strokeWidth="2" />
              </motion.g>
            </svg>
            <span className={styles.sceneLabel}>Drag to reorder by priority</span>
          </motion.div>
        ) : (
          /* Default Scene */
          <motion.svg
            viewBox="0 0 400 180"
            className={styles.sceneSvg}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <rect x="50" y="40" width="300" height="100" rx="8" fill="#fff" stroke="#1a1a1a" strokeWidth="2" />
            <line x1="70" y1="70" x2="330" y2="70" stroke="#e0e0e0" strokeWidth="2" />
            <line x1="70" y1="90" x2="330" y2="90" stroke="#e0e0e0" strokeWidth="2" />
            <line x1="70" y1="110" x2="280" y2="110" stroke="#e0e0e0" strokeWidth="2" />
          </motion.svg>
        )}
      </div>

      {/* QUESTION TEXT */}
      <div className={styles.questionText}>
        {gamifiedQuestion?.situation}
      </div>

      {/* ANSWER OPTIONS - Bottom */}
      <div className={isPriorityMode ? styles.optionsAreaFull : styles.optionsArea}>
        {!isPriorityMode ? (
          // Bloom's tier selection or standard options
          (isBlooms ? BLOOM_TIERS.slice(0, options.length) : options).map((item, index) => {
            const label = isBlooms ? options[index]?.label : item.label;
            const text = isBlooms ? options[index]?.text : item.text;
            const color = isBlooms ? item.color : accent;
            const isSelected = selected === label;

            return (
              <motion.button
                key={label || index}
                type="button"
                className={`${styles.choiceBtn} ${isSelected ? styles.selected : ''}`}
                style={{ '--tier': color }}
                initial={{ y: 30, opacity: 0 }}
                animate={{
                  y: 0,
                  opacity: selected && !isSelected ? 0.3 : 1
                }}
                transition={{ delay: 0.6 + index * 0.1, type: 'spring', stiffness: 100 }}
                whileHover={!selected ? { y: -2, borderColor: color } : {}}
                whileTap={!selected ? { scale: 0.98 } : {}}
                onClick={() => handleSelect(label)}
                disabled={!!selected}
              >
                <span className={styles.choiceLabel}>{label}</span>
                <span className={styles.choiceText}>{text}</span>
              </motion.button>
            );
          })
        ) : (
          // Priority drag mode
          <div className={styles.dragContainer}>
            <Reorder.Group
              axis="y"
              values={ranked}
              onReorder={handleReorder}
              className={styles.rankList}
            >
              {ranked.map((option, index) => (
                <Reorder.Item
                  key={option.label}
                  value={option}
                  className={styles.strategyCard}
                  style={{ borderLeftColor: accent }}
                >
                  <motion.div
                    className={styles.cardInner}
                    initial={{ x: -50, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: index * 0.08, type: 'spring' }}
                  >
                    <span className={styles.rankNumber}>{index + 1}</span>
                    <span className={styles.cardLabel}>{option.label}</span>
                    <span className={styles.cardText}>{option.actionText || option.text}</span>
                    <span className={styles.dragHandle}>⠿</span>
                  </motion.div>
                </Reorder.Item>
              ))}
            </Reorder.Group>

            <AnimatePresence>
              {hasReordered && !selected && (
                <motion.button
                  type="button"
                  className={styles.confirmBtn}
                  onClick={handleSubmit}
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  exit={{ y: 20, opacity: 0 }}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Confirm Priority Order
                </motion.button>
              )}
            </AnimatePresence>
          </div>
        )}
      </div>

      {/* Narration */}
      <p className={styles.narration}>{gamifiedQuestion?.narration}</p>

      <ConsequenceReveal
        visible={showOutcome}
        tone={selected === 'B' ? 'success' : 'warning'}
        message={selectedOption?.consequence || 'Pedagogical decision registered.'}
        duration={1600}
        onDone={() => {
          setShowOutcome(false);
          if (selected) onAnswer(selected);
        }}
      />
    </div>
  );
};

export default LessonBuilderEngine;
